import {useEffect, useState} from 'react';
import {Button, Form, InputGroup, Modal} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import {VpnAccount} from '../../Types/routerForms';
import {PASSWORD_REGEX, randomStrongPassword} from '../../Utils/random';

interface EditVpnPasswordModalProps {
  show: boolean;
  onHide: () => void;
  account: VpnAccount | null;
  onSave: (account: VpnAccount) => void;
}

const EditVpnPasswordModal = ({
  show,
  onHide,
  account,
  onSave,
}: EditVpnPasswordModalProps): JSX.Element => {
  const intl = useIntl();
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    if (show && account) {
      setPassword(account.password);
      setShowPwd(false);
      setValidated(false);
    }
  }, [show, account]);

  if (!account) return <></>;

  const isPasswordValid = PASSWORD_REGEX.test(password);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidated(true);
    if (!isPasswordValid) return;
    onSave({...account, password});
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VPN.EDIT_PWD_TITLE'})}
        </Modal.Title>
      </Modal.Header>
      <Form noValidate validated={validated} onSubmit={handleSubmit} id="editVpnPwdForm">
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VPN.LOGIN'})}</Form.Label>
            <Form.Control type="text" value={account.login} disabled />
          </Form.Group>
          <Form.Group>
            <Form.Label className="required">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VPN.PASSWORD'})}
            </Form.Label>
            <InputGroup hasValidation>
              <Form.Control
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                isInvalid={validated && !isPasswordValid}
                required
              />
              <Button
                variant="outline-secondary"
                type="button"
                onClick={() => setShowPwd((s) => !s)}
              >
                <i className={`fa-solid ${showPwd ? 'fa-eye-slash' : 'fa-eye'}`} />
              </Button>
              <Button
                variant="outline-secondary"
                type="button"
                onClick={() => setPassword(randomStrongPassword(12))}
              >
                <i className="fa-solid fa-dice" />
              </Button>
              <Form.Control.Feedback type="invalid">
                {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VALIDATION.PASSWORD'})}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            {intl.formatMessage({id: 'COMMON.CANCEL'})}
          </Button>
          <Button variant="primary" type="submit" form="editVpnPwdForm">
            {intl.formatMessage({id: 'COMMON.SAVE'})}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditVpnPasswordModal;
